// process.env safe client component
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Layers,
  Layout,
  Plus,
  Trash2,
  Move,
  Eye,
  Save,
  Check,
  Sparkles,
  RefreshCw,
  Sliders,
  Maximize2,
  Minimize2,
  Compass,
  FileText,
  Landmark,
  Receipt,
  Scale,
  Bot,
  ExternalLink,
  Code,
  Shield,
  HelpCircle,
  GripVertical,
  Globe,
  Copy,
  QrCode,
  CheckCircle2,
  AlertCircle,
  Lock,
  Share2,
  Zap
} from 'lucide-react';

export interface PortalModule {
  moduleId: string;
  nodeId: string;
  title: string;
  route: string;
  category?: string;
  description?: string;
  icon?: string;
  formSchema?: any;
  knowledgeContext?: string;
  isCustomizable?: boolean;
}

export interface PortalLayoutBlock {
  id: string;
  moduleId: string;
  nodeId: string;
  title: string;
  route: string;
  colSpan: 12 | 8 | 6 | 4;
  style: 'glass_card' | 'glow_border' | 'minimal';
  customInstructions?: string;
  isVisible: boolean;
}

export interface PortalThemeConfig {
  mode: 'dark_glass' | 'cyber_neon' | 'midnight_blue';
  primaryColor: string;
  accentColor: string;
  heroTitle: string;
  heroSubtitle: string;
  showBranding: boolean;
}

interface TenetPortalBuilderProps {
  token: string;
  tenant: any;
  onDeployCode?: () => void;
}

export const TenetPortalBuilder: React.FC<TenetPortalBuilderProps> = ({
  token,
  tenant,
  onDeployCode
}) => {
  const [availableModules, setAvailableModules] = useState<PortalModule[]>([]);
  const [layoutBlocks, setLayoutBlocks] = useState<PortalLayoutBlock[]>([]);
  const [themeConfig, setThemeConfig] = useState<PortalThemeConfig>({
    mode: 'dark_glass',
    primaryColor: '#06b6d4',
    accentColor: '#6366f1',
    heroTitle: `${tenant?.name || 'Client'} Enterprise Concierge Portal`,
    heroSubtitle: 'Autonomous Advisory & Self-Service Operations Powered by Luna AI',
    showBranding: true
  });

  // Subdomain & Publishing State
  const [subdomainInput, setSubdomainInput] = useState<string>('');
  const [subdomainStatus, setSubdomainStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'error'>('idle');
  const [subdomainMessage, setSubdomainMessage] = useState<string>('');
  const [claimedSubdomain, setClaimedSubdomain] = useState<string | null>(null);
  const [claimedSubdomainUrl, setClaimedSubdomainUrl] = useState<string | null>(null);
  const [showPublishModal, setShowPublishModal] = useState<boolean>(false);
  const [copiedUrl, setCopiedUrl] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [savedSuccess, setSavedSuccess] = useState<boolean>(false);
  const [previewMode, setPreviewMode] = useState<boolean>(false);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [draggedModule, setDraggedModule] = useState<PortalModule | null>(null);
  const [draggedBlockIndex, setDraggedBlockIndex] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const debounceTimerRef = useRef<any>(null);

  const showNotification = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const authHeaders = useCallback(() => ({
    'Authorization': token.startsWith('Bearer ') ? token : `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  // ── 1. Fetch Extracted Section Modules & Saved Layout ────────────────────
  const loadPortalData = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Fetch extracted SectionNodes from active flow
      const modRes = await fetch('/api/tenet/portal/modules', {
        headers: authHeaders()
      });
      const modData = await modRes.json();
      
      let fetchedModules: PortalModule[] = [];
      if (modData.success && Array.isArray(modData.modules)) {
        fetchedModules = modData.modules;
        setAvailableModules(fetchedModules);
      }

      // 2. Fetch saved layout configuration & claimed subdomain
      const layoutRes = await fetch('/api/tenet/portal/layout', {
        headers: authHeaders()
      });
      const layoutData = await layoutRes.json();

      if (layoutData.success) {
        if (layoutData.layout) {
          if (Array.isArray(layoutData.layout.blocks)) {
            setLayoutBlocks(layoutData.layout.blocks);
          }
          if (layoutData.layout.theme) {
            setThemeConfig(prev => ({ ...prev, ...layoutData.layout.theme }));
          }
        } else if (fetchedModules.length > 0) {
          // Generate sensible default layout from available modules
          const defaultBlocks: PortalLayoutBlock[] = fetchedModules.map((m, idx) => ({
            id: `block_${m.nodeId}_${idx}`,
            moduleId: m.moduleId,
            nodeId: m.nodeId,
            title: m.title,
            route: m.route,
            colSpan: idx === 0 && fetchedModules.length > 2 ? 12 : 6,
            style: 'glass_card',
            isVisible: true
          }));
          setLayoutBlocks(defaultBlocks);
        }

        if (layoutData.subdomain) {
          setClaimedSubdomain(layoutData.subdomain);
          setSubdomainInput(layoutData.subdomain);
          setClaimedSubdomainUrl(layoutData.subdomainUrl || `https://${layoutData.subdomain}.kangavena.com`);
          setSubdomainStatus('available');
        } else {
          // Default suggestion based on tenant name
          const initialSuggestion = (tenant?.name || '')
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '')
            .slice(0, 30);
          if (initialSuggestion) {
            setSubdomainInput(initialSuggestion);
          }
        }
      }
    } catch (err: any) {
      console.warn('[TenetPortalBuilder] Load error:', err);
      showNotification('Failed to load portal configuration.');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, tenant?.name]);

  useEffect(() => {
    loadPortalData();
  }, [loadPortalData]);

  // ── 2. Real-Time Subdomain Availability Checker ─────────────────────────
  const handleSubdomainChange = (val: string) => {
    const clean = val.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSubdomainInput(clean);

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    if (!clean || clean.length < 2) {
      setSubdomainStatus('idle');
      setSubdomainMessage('');
      return;
    }

    if (clean === claimedSubdomain) {
      setSubdomainStatus('available');
      setSubdomainMessage(`✨ Currently active for your portal: ${clean}.kangavena.com`);
      return;
    }

    setSubdomainStatus('checking');
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/tenet/portal/check-subdomain?slug=${encodeURIComponent(clean)}`, {
          headers: authHeaders()
        });
        const data = await res.json();
        if (data.success && data.available) {
          setSubdomainStatus('available');
          setSubdomainMessage(data.message || `🎉 "${clean}.kangavena.com" is available!`);
        } else {
          setSubdomainStatus('taken');
          setSubdomainMessage(data.error || `"${clean}.kangavena.com" is already claimed.`);
        }
      } catch (err) {
        setSubdomainStatus('error');
        setSubdomainMessage('Error checking subdomain availability.');
      }
    }, 450);
  };

  // ── 3. Save Layout & 1-Click Publish to Subdomain ────────────────────────
  const handleSaveLayout = async (publishSubdomain = false) => {
    setSaving(true);
    try {
      const payload = {
        blocks: layoutBlocks,
        theme: themeConfig
      };

      const res = await fetch('/api/tenet/portal/layout', {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          layout: payload,
          subdomain: subdomainInput.trim() || undefined
        })
      });
      const data = await res.json();

      if (data.success) {
        setSavedSuccess(true);
        if (data.subdomain) {
          setClaimedSubdomain(data.subdomain);
          setClaimedSubdomainUrl(data.subdomainUrl || `https://${data.subdomain}.kangavena.com`);
        }
        showNotification(publishSubdomain ? '🚀 Client Portal published live to Subdomain!' : 'Portal layout saved successfully!');
        if (publishSubdomain) {
          setShowPublishModal(true);
        }
        setTimeout(() => setSavedSuccess(false), 2500);
      } else {
        showNotification(data.error || 'Failed to save layout.');
      }
    } catch (err: any) {
      console.error('[TenetPortalBuilder] Save error:', err);
      showNotification('Error saving portal layout.');
    } finally {
      setSaving(false);
    }
  };

  // ── 4. Drag & Drop Handlers (Native HTML5 API) ──────────────────────────
  const handleDragStartFromSidebar = (mod: PortalModule) => {
    setDraggedModule(mod);
    setDraggedBlockIndex(null);
  };

  const handleDragStartFromCanvas = (index: number) => {
    setDraggedBlockIndex(index);
    setDraggedModule(null);
  };

  const handleDropOnCanvas = (e: React.DragEvent, targetIndex?: number) => {
    e.preventDefault();

    if (draggedModule) {
      // Adding a new module from sidebar
      const newBlock: PortalLayoutBlock = {
        id: `block_${draggedModule.nodeId}_${Date.now()}`,
        moduleId: draggedModule.moduleId,
        nodeId: draggedModule.nodeId,
        title: draggedModule.title,
        route: draggedModule.route,
        colSpan: 6,
        style: 'glass_card',
        isVisible: true
      };

      if (typeof targetIndex === 'number') {
        const next = [...layoutBlocks];
        next.splice(targetIndex, 0, newBlock);
        setLayoutBlocks(next);
      } else {
        setLayoutBlocks(prev => [...prev, newBlock]);
      }

      showNotification(`Added "${draggedModule.title}" to portal canvas.`);
      setDraggedModule(null);
    } else if (draggedBlockIndex !== null && typeof targetIndex === 'number') {
      // Reordering existing blocks on canvas
      const next = [...layoutBlocks];
      const [moved] = next.splice(draggedBlockIndex, 1);
      next.splice(targetIndex, 0, moved);
      setLayoutBlocks(next);
      setDraggedBlockIndex(null);
    }
  };

  const removeBlock = (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setLayoutBlocks(prev => prev.filter(b => b.id !== id));
    if (selectedBlockId === id) setSelectedBlockId(null);
    showNotification('Module block removed from canvas.');
  };

  const updateBlockColSpan = (id: string, colSpan: 12 | 8 | 6 | 4, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setLayoutBlocks(prev => prev.map(b => b.id === id ? { ...b, colSpan } : b));
  };

  const copyPublishedLink = () => {
    const liveUrl = claimedSubdomainUrl || `https://kangavena.com/portal/${tenant?.apiKey || tenant?.id}`;
    navigator.clipboard.writeText(liveUrl);
    setCopiedUrl(true);
    showNotification('📋 Portal link copied to clipboard!');
    setTimeout(() => setCopiedUrl(false), 3000);
  };

  const selectedBlock = layoutBlocks.find(b => b.id === selectedBlockId);
  const activePortalLiveUrl = claimedSubdomainUrl || `https://kangavena.com/portal/${tenant?.apiKey || tenant?.id || ''}`;

  return (
    <div className="h-full w-full flex flex-col bg-[#090D16] text-slate-100 font-sans select-none overflow-hidden relative">
      
      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-5 right-5 z-[100] bg-[#121826]/95 border border-cyan-500/40 text-cyan-200 px-4 py-2.5 rounded-xl shadow-2xl backdrop-blur-xl flex items-center gap-2.5 animate-in fade-in slide-in-from-top-3">
          <Sparkles className="w-4 h-4 text-cyan-400 animate-spin" />
          <span className="text-xs font-medium">{toast}</span>
        </div>
      )}

      {/* ── 1-CLICK PUBLISH SUCCESS MODAL ────────────────────────────────── */}
      {showPublishModal && (
        <div className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-[#0D121F] border border-cyan-500/40 rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl space-y-5 animate-in zoom-in-95">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-2xl bg-emerald-500/20 border border-emerald-400/40 text-emerald-300">
                  <Globe size={22} />
                </div>
                <div>
                  <h3 className="text-base font-black text-white">Client Portal Live!</h3>
                  <p className="text-xs text-emerald-400 font-mono flex items-center gap-1">
                    <Lock size={11} /> Cloudflare Wildcard SSL Active
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowPublishModal(false)}
                className="p-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-white/10 transition cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="p-4 rounded-2xl bg-slate-950/80 border border-cyan-500/30 space-y-3">
              <label className="text-[11px] font-mono uppercase text-slate-400 font-bold block">
                Live Public Client Portal URL
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={activePortalLiveUrl}
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-cyan-300 font-mono text-xs font-bold focus:outline-none select-all"
                />
                <button
                  type="button"
                  onClick={copyPublishedLink}
                  className="px-3.5 py-2 rounded-xl bg-cyan-500 text-slate-950 font-bold text-xs flex items-center gap-1.5 hover:bg-cyan-400 transition shrink-0 cursor-pointer shadow-[0_0_12px_rgba(6,182,212,0.4)]"
                >
                  {copiedUrl ? <Check size={14} /> : <Copy size={14} />}
                  <span>{copiedUrl ? 'Copied' : 'Copy'}</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="p-3 rounded-2xl bg-slate-900/60 border border-white/5 space-y-1">
                <span className="text-slate-400 font-medium block">Multi-Tenant Isolation</span>
                <span className="text-emerald-400 font-mono text-[11px] font-bold">Zero-Trust Protected</span>
              </div>
              <div className="p-3 rounded-2xl bg-slate-900/60 border border-white/5 space-y-1">
                <span className="text-slate-400 font-medium block">Voice Intake Engine</span>
                <span className="text-cyan-400 font-mono text-[11px] font-bold">Luna Multimodal Live</span>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-2">
              <a
                href={activePortalLiveUrl}
                target="_blank"
                rel="noreferrer"
                className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-slate-950 font-bold text-xs flex items-center gap-2 hover:from-cyan-400 hover:to-blue-500 transition shadow-[0_0_20px_rgba(6,182,212,0.4)]"
              >
                <span>Launch Client Portal</span>
                <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ── TOP ACTION BAR (HUD) ────────────────────────────────────────── */}
      <header className="min-h-[56px] sm:h-16 py-2 px-3 sm:px-6 bg-[#0D121F] border-b border-slate-800/80 flex flex-wrap items-center justify-between gap-2.5 shrink-0 z-20">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-400">
            <Layout size={18} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-black text-white font-sans tracking-tight">Client Portal & Subdomain Builder</h2>
              <span className="px-2 py-0.5 rounded-full text-[9px] font-bold font-mono bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
                HEADLESS SAAS
              </span>
            </div>
            <p className="text-[11px] text-slate-400">Publish custom white-labeled client dashboards on *.kangavena.com</p>
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => setPreviewMode(!previewMode)}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer border ${
              previewMode 
                ? 'bg-cyan-500 text-slate-950 border-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.4)]'
                : 'bg-slate-900/90 text-slate-300 border-white/10 hover:bg-slate-800 hover:text-white'
            }`}
          >
            <Eye size={14} />
            <span>{previewMode ? 'Exit Preview' : 'Live Preview'}</span>
          </button>

          <button
            type="button"
            onClick={() => handleSaveLayout(false)}
            disabled={saving}
            className="px-4 py-1.5 rounded-xl text-xs font-bold bg-slate-900 hover:bg-slate-800 text-slate-200 border border-white/10 flex items-center gap-1.5 transition cursor-pointer shadow-sm disabled:opacity-50"
          >
            {saving ? <RefreshCw size={13} className="animate-spin text-cyan-400" /> : savedSuccess ? <Check size={13} className="text-emerald-400" /> : <Save size={13} />}
            <span>{saving ? 'Saving...' : savedSuccess ? 'Saved!' : 'Save Draft'}</span>
          </button>

          <button
            type="button"
            onClick={() => handleSaveLayout(true)}
            disabled={saving || subdomainStatus === 'taken'}
            className="px-5 py-1.5 rounded-xl text-xs font-black bg-gradient-to-r from-cyan-400 via-teal-400 to-emerald-400 hover:from-cyan-300 hover:to-emerald-300 text-slate-950 flex items-center gap-1.5 transition cursor-pointer shadow-[0_0_20px_rgba(34,211,238,0.5)] disabled:opacity-50"
          >
            <Zap size={14} className="fill-slate-950" />
            <span>Publish Subdomain</span>
          </button>
        </div>
      </header>

      {/* ── SUBDOMAIN CLAIMING & INSTANT SSL BANNER ────────────────────── */}
      <div className="bg-[#080C14] border-b border-cyan-500/20 px-3 sm:px-6 py-2.5 flex flex-wrap items-center justify-between gap-2.5 text-xs">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-cyan-300 font-mono font-bold text-[11px]">
            <Globe size={14} className="text-cyan-400" />
            <span>SUBDOMAIN:</span>
          </div>

          <div className="flex items-center gap-1 bg-slate-900 border border-slate-700/80 rounded-xl px-2.5 py-1 focus-within:border-cyan-400 transition">
            <span className="text-slate-400 font-mono text-xs">https://</span>
            <input
              type="text"
              value={subdomainInput}
              onChange={(e) => handleSubdomainChange(e.target.value)}
              placeholder="yourbusiness"
              className="bg-transparent text-cyan-300 font-mono font-bold text-xs focus:outline-none w-32 sm:w-44"
            />
            <span className="text-slate-400 font-mono text-xs">.kangavena.com</span>
          </div>

          {/* Subdomain Status Indicator */}
          {subdomainStatus === 'checking' && (
            <span className="text-slate-400 text-[11px] font-mono flex items-center gap-1 animate-pulse">
              <RefreshCw size={12} className="animate-spin text-cyan-400" /> Checking availability...
            </span>
          )}
          {subdomainStatus === 'available' && (
            <span className="text-emerald-400 text-[11px] font-mono font-bold flex items-center gap-1 bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5 rounded-lg">
              <CheckCircle2 size={12} /> Available
            </span>
          )}
          {subdomainStatus === 'taken' && (
            <span className="text-rose-400 text-[11px] font-mono font-bold flex items-center gap-1 bg-rose-500/10 border border-rose-500/30 px-2 py-0.5 rounded-lg">
              <AlertCircle size={12} /> Already Claimed
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-[10px] font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/25 px-2.5 py-1 rounded-lg">
            <Lock size={11} /> Cloudflare Wildcard SSL Active
          </div>
          {claimedSubdomainUrl && (
            <a
              href={claimedSubdomainUrl}
              target="_blank"
              rel="noreferrer"
              className="text-cyan-300 hover:text-cyan-200 text-xs font-mono font-bold flex items-center gap-1 underline underline-offset-2"
            >
              <span>{claimedSubdomainUrl}</span>
              <ExternalLink size={12} />
            </a>
          )}
        </div>
      </div>

      {/* ── MAIN WORKSPACE (LEFT PALETTE + CENTER CANVAS + RIGHT INSPECTOR) ── */}
      <div className="tenet-mobile-split flex-1 flex overflow-hidden relative">

        {/* ── LEFT SIDEBAR: AVAILABLE SECTION MODULES PALETTE ──────────── */}
        {!previewMode && (
          <aside className="w-full md:w-72 max-h-56 md:max-h-none bg-[#0D121F] border-b md:border-b-0 md:border-r border-slate-800/80 flex flex-col shrink-0 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4 z-10 shadow-2xl">
            <div className="flex items-center justify-between pb-2 border-b border-slate-800">
              <span className="text-xs font-black uppercase text-slate-400 tracking-wider flex items-center gap-1.5 font-mono">
                <Layers size={13} className="text-cyan-400" /> SECTION MODULES
              </span>
              <span className="text-[10px] font-mono text-cyan-300 bg-cyan-500/10 px-2 py-0.5 rounded-full">
                {availableModules.length} Active
              </span>
            </div>

            <p className="text-[11px] text-slate-400 leading-relaxed font-sans">
              Drag modules onto the canvas to construct your client self-service interface:
            </p>

            {/* Draggable Module Cards */}
            <div className="space-y-2.5">
              {availableModules.map((mod) => (
                <div
                  key={mod.moduleId}
                  draggable
                  onDragStart={() => handleDragStartFromSidebar(mod)}
                  className="p-3 rounded-2xl bg-gradient-to-r from-[#111827] to-[#0F172A] border border-cyan-500/25 hover:border-cyan-400/60 cursor-grab active:cursor-grabbing transition-all hover:scale-[1.02] shadow-md group space-y-1.5"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="p-1 rounded-lg bg-cyan-500/15 text-cyan-400 border border-cyan-500/30">
                        <Compass size={13} />
                      </div>
                      <span className="text-xs font-bold text-white group-hover:text-cyan-300 transition">
                        {mod.title}
                      </span>
                    </div>
                    <GripVertical size={14} className="text-slate-500 group-hover:text-cyan-400 transition" />
                  </div>

                  <div className="flex items-center justify-between text-[10px] font-mono text-slate-400">
                    <span className="bg-slate-900/80 px-2 py-0.5 rounded border border-white/5 text-cyan-300/80">
                      {mod.route}
                    </span>
                    <span className="text-slate-500">Drag to place</span>
                  </div>
                </div>
              ))}

              {availableModules.length === 0 && !loading && (
                <div className="p-4 rounded-2xl bg-slate-900/60 border border-white/5 text-center space-y-2 text-xs text-slate-400">
                  <Bot size={24} className="mx-auto text-cyan-400/60" />
                  <p>No Section Nodes found in your active Luna Flow.</p>
                  <p className="text-[10px] text-slate-500">Create SectionNodes in the Master Flow Builder to populate modules.</p>
                </div>
              )}
            </div>
          </aside>
        )}

        {/* ── CENTER CANVAS: 12-COLUMN DRAG & DROP GRID ─────────────────── */}
        <main
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => handleDropOnCanvas(e)}
          className="flex-1 overflow-y-auto p-3 sm:p-6 md:p-8 bg-gradient-to-b from-[#070A12] to-[#090D16] space-y-4 sm:space-y-6 tenet-mobile-scroll"
        >
          {/* Portal Hero Banner Customizer */}
          <div className="rounded-3xl bg-[#0D1324]/80 border border-cyan-500/30 p-6 sm:p-8 relative overflow-hidden backdrop-blur-xl shadow-2xl space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-widest text-cyan-400 font-mono font-bold flex items-center gap-1.5">
                <Shield size={13} /> {tenant?.name || 'Enterprise'} CLIENT PORTAL
              </span>
              <span className="text-emerald-400 text-xs font-mono flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live Dynamic Context
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight font-sans">
              {themeConfig.heroTitle}
            </h1>
            <p className="text-xs sm:text-sm text-slate-300 max-w-2xl leading-relaxed">
              {themeConfig.heroSubtitle}
            </p>
          </div>

          {/* 12-Column Responsive Layout Grid */}
          <div className="grid grid-cols-12 gap-5">
            {layoutBlocks.map((block, idx) => {
              const colSpanClass =
                block.colSpan === 12
                  ? 'col-span-12'
                  : block.colSpan === 8
                  ? 'col-span-12 lg:col-span-8'
                  : block.colSpan === 6
                  ? 'col-span-12 md:col-span-6'
                  : 'col-span-12 sm:col-span-6 lg:col-span-4';

              const isSelected = selectedBlockId === block.id;

              return (
                <div
                  key={block.id}
                  draggable={!previewMode}
                  onDragStart={() => handleDragStartFromCanvas(idx)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handleDropOnCanvas(e, idx)}
                  onClick={() => setSelectedBlockId(block.id)}
                  className={`${colSpanClass} rounded-3xl p-5 transition-all duration-200 cursor-pointer relative group ${
                    isSelected
                      ? 'bg-[#11182B] border-2 border-cyan-400 shadow-[0_0_30px_rgba(6,182,212,0.35)]'
                      : 'bg-[#0D121F]/90 hover:bg-[#101728] border border-cyan-500/25 hover:border-cyan-400/50 shadow-xl'
                  }`}
                >
                  {/* Block Header & Action Controls */}
                  <div className="flex items-center justify-between pb-3 border-b border-white/5">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="p-1.5 rounded-xl bg-cyan-500/15 border border-cyan-400/30 text-cyan-300 shrink-0">
                        <Compass size={14} />
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-xs font-black text-white truncate">{block.title}</h4>
                        <span className="font-mono text-[10px] text-cyan-300/80 block truncate">{block.route}</span>
                      </div>
                    </div>

                    {!previewMode && (
                      <div className="flex items-center gap-1 shrink-0 opacity-80 group-hover:opacity-100 transition">
                        {/* Width adjustment buttons */}
                        <button
                          type="button"
                          onClick={(e) => updateBlockColSpan(block.id, block.colSpan === 12 ? 6 : 12, e)}
                          className="p-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-[10px] font-mono px-1.5"
                          title="Toggle Width"
                        >
                          {block.colSpan === 12 ? '50%' : '100%'}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => removeBlock(block.id, e)}
                          className="p-1 rounded-lg bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 transition cursor-pointer"
                          title="Remove Block"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Block Content Mock / Live Area */}
                  <div className="pt-4 space-y-3">
                    <div className="p-3.5 rounded-2xl bg-[#080C14] border border-white/5 space-y-2">
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-slate-400 font-medium">Autonomous GenUI Interface</span>
                        <span className="text-emerald-400 font-mono text-[10px] flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Linked
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-400 leading-relaxed font-sans">
                        When clients enter this section, Luna renders the interactive intake schema and executes voice ghost-typing for route <code className="text-cyan-300 font-mono">{block.route}</code>.
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Empty Drop Target when no blocks exist */}
            {layoutBlocks.length === 0 && !loading && (
              <div className="col-span-12 border-2 border-dashed border-cyan-500/30 rounded-3xl p-12 text-center space-y-3 bg-[#0D121F]/40">
                <Layout size={32} className="mx-auto text-cyan-400/60" />
                <h3 className="text-sm font-black text-white">Your Portal Canvas is Empty</h3>
                <p className="text-xs text-slate-400 max-w-md mx-auto">
                  Drag Section Modules from the left palette to construct your custom client self-service portal.
                </p>
              </div>
            )}
          </div>
        </main>

        {/* ── RIGHT PROPERTY INSPECTOR ──────────────────────────────────── */}
        {!previewMode && selectedBlock && (
          <aside className="fixed md:static inset-y-0 right-0 w-full sm:w-80 bg-[#0D121F] border-l border-slate-800/80 p-4 sm:p-5 space-y-4 sm:space-y-5 overflow-y-auto shrink-0 shadow-2xl z-30 md:z-10 tenet-mobile-scroll">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <span className="text-xs font-black uppercase text-slate-400 tracking-wider flex items-center gap-1.5 font-mono">
                <Sliders size={13} className="text-cyan-400" /> BLOCK INSPECTOR
              </span>
              <button
                type="button"
                onClick={() => setSelectedBlockId(null)}
                className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-300">Display Title</label>
                <input
                  type="text"
                  value={selectedBlock.title}
                  onChange={(e) => {
                    const val = e.target.value;
                    setLayoutBlocks(prev => prev.map(b => b.id === selectedBlock.id ? { ...b, title: val } : b));
                  }}
                  className="w-full bg-[#080C14] border border-cyan-500/30 rounded-xl px-3.5 py-2 text-xs text-cyan-200 focus:outline-none focus:border-cyan-400"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-300">Route Match Pattern</label>
                <input
                  type="text"
                  value={selectedBlock.route}
                  onChange={(e) => {
                    const val = e.target.value;
                    setLayoutBlocks(prev => prev.map(b => b.id === selectedBlock.id ? { ...b, route: val } : b));
                  }}
                  className="w-full bg-[#080C14] border border-cyan-500/30 rounded-xl px-3.5 py-2 text-xs text-cyan-200 font-mono focus:outline-none focus:border-cyan-400"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-300">Grid Column Width</label>
                <div className="grid grid-cols-4 gap-1.5">
                  {[4, 6, 8, 12].map((col) => (
                    <button
                      key={col}
                      type="button"
                      onClick={() => updateBlockColSpan(selectedBlock.id, col as any)}
                      className={`py-1.5 rounded-lg text-xs font-mono font-bold transition border ${
                        selectedBlock.colSpan === col
                          ? 'bg-cyan-500 text-slate-950 border-cyan-400'
                          : 'bg-slate-900 text-slate-300 border-white/10 hover:bg-slate-800'
                      }`}
                    >
                      {col}/12
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        )}

      </div>
    </div>
  );
};

export default TenetPortalBuilder;
